// Hooks & Utilities
import { Link, useNavigate, useParams } from "react-router";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useAuth } from "../../../providers/auth-context";
import { handleApplicantCityAndState } from "../../../utilities/strings/handleApplicantCityAndState";
import handleFullnameCombination from "../../../utilities/strings/handleFullnameCombination";
import handlePhoneStringSanitization from "../../../utilities/strings/handlePhoneStringSanitization";

// Assets
import { FaChevronLeft as BackIcon, FaMapMarkerAlt as LocationIcon } from "react-icons/fa";

import {
  MdPhoneInTalk as PhoneIcon,
  MdOutlineAlternateEmail as ApplicationMailIcon,
} from "react-icons/md";

import EmailIcon from "../../../assets/images/icons/applications-email-application-icon.svg?react";
import PrintIcon from "../../../assets/images/icons/applications-print-application-icon.svg?react";
import RefreshIcon from "../../../assets/images/icons/applications-refresh-page-icon.svg?react";

// Components
import ApplicationBucketMovement from "../../../components/Applications/Buckets/ApplicationBucketMovement";
import Tooltip from "../../../components/Tooltip/Tooltip";
import Card from "../../../components/Card/Card";
import ApplicationRating from "../../../components/Applications/ApplicationRating";
import ApplicationPhoto from "../../../components/Applications/ApplicationPhoto";
import ApplicationArchive from "../../../components/Applications/Buckets/ApplicationArchive";
import ApplicationFavorite from "../../../components/Applications/Buckets/ApplicationFavorite";
import ApplicationSendText from "../../../components/Applications/Socials/ApplicationSendText";
import ApplicationResume from "../../../components/Applications/Socials/ApplicationResume";
import ApplicationLinkedIn from "../../../components/Applications/Socials/ApplicationLinkedIn";
import ApplicationScheduleMeeting from "../../../components/Applications/Socials/ApplicationScheduleMeeting";
import Button from "../../../components/Button/Button";
import AppointmentsListItem from "../../../components/Appointments/AppointmentsListItem";
import ApplicationComment from "../../../components/Applications/ApplicationComment";
import PermissionCheckComponentWrapper from "../../../components/Wrappers/PermissionCheckComponentWrapper";
import ApplicationExtraInformation from "../../../features/Applications/ApplicationExtraInformation";
import ApplicationSourceAdmin from "../../../components/Applications/ApplicationSourceAdmin";
import ApplicationDateSection from "../../../components/Applications/ApplicationDateSection";
import ApplicationSendEmail from "../../../components/Applications/Socials/ApplicationSendEmail";
import ApplicationsDetailsAdminActions from "../../../components/Applications/ApplicationsDetailsAdminActions";
import ApplicationSBCADetails from "../../../components/Applications/ApplicationSBCADetails";
import ApplicationGeneratedInterviewQuestions from "../../../features/Applications/ApplicationGeneratedInterviewQuestions";
import ApplicantSummary from "../../../features/Applications/ApplicantSummary";

// Interfaces
import { AppointmentResponseFields } from "../../../api/Appointments/interfaces";
import {
  ApplicationCommentsFields,
  ApplicationDetailsFields,
} from "../../../api/Applications/interfaces";
import { AnimatePresence } from "framer-motion";
import { UserRoleNames } from "../../../interfaces/global";
import ProductTourWrapper from "../../../components/ProductTour/ProductTourWrapper";
import { useTour } from "../../../providers/tour-context";

const TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA: ApplicationDetailsFields = {
  id: 0,
  source: "Other",
  comments_avg_rating: null,
  schedule_meeting: null,
  meeting_link: "",
  encrypted_application_id: "",
  applicant: {
    id: 0,
    sbca: {
      score: 4.69,
      caregiver: {
        score: 34.84,
        color: "yellow",
        emotions: "Primary",
        characteristics:
          "Sensitive to negativity, Concerned about how others see him/her, Customer advocate, Pretender.",
      },
      thinker: {
        score: 32.58,
        color: "green",
        ego: "Secondary",
        characteristics: "Neat, Ambitious, Somewhat persuasive, Initiator.",
        driven: "2.40",
      },
      processor: {
        score: 32.58,
        color: "green",
        logic: "Secondary",
        characteristics:
          "Average aptitude, Learns through repetition, Unorganized to somewhat organized.",
      },
    },
    employer: {},
    sbca_report_identifier: "",
    sbca_request_date: null,
    has_ai_sbca_summary: true,
    has_ai_resume_summary: true,
    first_name: "John",
    middle_name: null,
    last_name: "Doe",
    email: "john@doe.com",
    phone: "",
    address: "Example Address",
    city: "New York",
    state: "NY",
    zip_code: "10001",
    photo: "",
    custom_values: {
      country: "US",
    },
  },
  form_name: "Short Application Form",
  appointments: [],
  comments: [],
  resume: "",
  resume_request_date: "",
  ai_resume_summary: {
    resume_summary:
      "### Position Fitness Assessment: \n\n**Overall Score:** 4.69  \n**Caregiver Score:** 34.84  \n**Thinker Score:** 32.58  \n**Processor Score:** 32.58  \n\n#### Ideal Work Environment\nBased on this profile, the ideal work environment would be one that emphasizes collaboration, support, and clear communication. The individual is sensitive to negativity and concerned about how others perceive them, suggesting that they would thrive in a positive, team-oriented atmosphere where feedback is constructive and encouragement is prevalent. \n\nThe moderate scores in the Thinker and Processor categories imply that the person enjoys being organized but may require some structure to help them improve efficiency. A role that allows for creativity and initiative—while providing a balance of routine tasks and opportunities for new challenges—would be beneficial. \n\nThis individual may perform best in environments where they can advocate for customers or clients, ensuring that their needs are met while also allowing them to express their ambitious side.\n\n#### Final Judgement: Mixed Fit\nThe profile suggests a mix of strengths and areas for potential concern. While the individual shows strong caregiving qualities, their higher-than-ideal score in this area might indicate a risk of burnout or overextending themselves for others. Their ambition and initiative are positive traits, but their unorganized tendencies could hold them back in fast-paced environments with less structure.",
    is_resume_regenerated: false,
    last_regenerated_resume_date: 1724161636,
    resume_interview_questions:
      '1. Sensitive to Negativity: "Can you describe a time when you encountered negativity in the workplace? How did you handle that situation, and what was the outcome?"\n   \n2. Customer Advocate: "How do you ensure that the needs of customers or clients are met in your work? Can you give an example of a time you went above and beyond for a customer?"\n   \n3. Neat and Organized: "You mentioned that you tend to be somewhat unorganized. What strategies or tools do you use to keep yourself on track and ensure your work is completed efficiently?"\n\n4. Ambitious Initiator: "Can you share an example of a project or initiative you took the lead on? What motivated you to take the initiative, and what were the results?"\n\n5. Concerned about Perception: "How do you approach feedback from colleagues or supervisors? Can you give an instance where feedback impacted your work or personal development?"\n\nThese questions aim to uncover the candidate\'s experiences and coping strategies related to their assessment scores while providing insight into their fit within the potential work environment.',
  },
  ai_sbca_summary: {
    sbca_summary:
      "### Summary\nThe candidate's resume presents a breadth of experience, emphasizing strengths in communication, problem-solving, and teamwork. They have demonstrated a consistent ability to engage with diverse stakeholders and navigate complex projects. The resume mentions a proactive approach to challenges, showcasing adaptability and a commitment to delivering high-quality outcomes. Their background reflects a solid foundation in both technical and interpersonal skills, which are essential in the current job market.\n\nAdditionally, the candidate has highlighted expertise in various relevant domains, including project management and operational efficiency. While the document lacks specific metrics or quantifiable achievements, it suggests a well-rounded professional background. This indicates their capability to contribute to team dynamics and drive initiatives that align with organizational goals. Overall, the candidate appears to be a versatile individual with potential to thrive in collaborative environments.\n\n### Areas of Strength\n1. **Communication Skills**: The candidate has a strong ability to articulate ideas clearly and work effectively with diverse teams.\n2. **Problem-Solving Capabilities**: Demonstrated adaptability in handling challenges and formulating solutions in dynamic environments.\n3. **Team Collaboration**: Proven track record of working well within teams, fostering a spirit of cooperation and collective achievement.\n4. **Project Management**: Familiarity with managing timelines and resources, contributing to project success.\n5. **Interpersonal Relationships**: Ability to build rapport with colleagues and stakeholders, enhancing work relationships.\n\n### Areas of Concern\n1. **Lack of Quantifiable Achievements**: The resume does not provide specific metrics or accomplishments that demonstrate the impact of the candidate's contributions.\n2. **Unclear Job Titles and Roles**: The absence of clearly defined job titles and responsibilities may lead to confusion regarding the candidate's level of expertise and experience.\n3. **Limited Technical Skills**: Depending on the role, the lack of detailed technical skills or certifications may be a concern for positions requiring specific competencies.\n4. **Generic Language**: The use of vague phrases may detract from the candidate's uniqueness and ability to stand out in a competitive job market.\n5. **Potential Gaps in Experience**: It is unclear if there are any gaps in employment or relevant experience that could affect the candidate's suitability for certain positions.\n\n### Types of Positions This Candidate Could Excel In\n1. **Project Coordinator**: Leveraging their organizational skills and ability to manage timelines and resources effectively.\n2. **Team Facilitator**: Focusing on collaboration and communication within teams to enhance project outcomes.\n3. **Customer Relations Specialist**: Utilizing their interpersonal skills to manage relationships with clients or stakeholders effectively.\n4. **Administrative Support Roles**: Supporting teams and projects through organizational and logistical assistance.\n5. **Junior Business Analyst**: Engaging in problem-solving and analysis to support business initiatives and improve processes.",
    is_sbca_regenerated: false,
    last_regenerated_sbca_date: 1724161646,
    sbca_interview_questions:
      "1. Can you provide an example of a challenging project you managed and how you approached problem-solving during that time?\n2. How do you ensure effective communication within your team, especially when faced with diverse perspectives?\n3. Describe a situation where you had to adapt quickly to changes in a project. What steps did you take to maintain progress?\n4. What strategies do you use to build strong relationships with colleagues and stakeholders?\n5. Can you share a specific instance where your contributions significantly impacted a project or team outcome?",
  },
  linkedin: null,
  bucket: ["Current"],
  registration_date: `${format(new Date(), "yyyy-MM-dd")}T10:00:00.000000Z`,
  position: "Body Shop Painter",
  text_message_consent: false,
  is_hidden: false,
  hired_date: null,
  hr_compliant_application: {
    has_form: false,
    application: null,
    application_request_date: null,
  },
};

const TourAiSummary = () => {
  const { user } = useAuth();
  const { applicationID } = useParams();
  const { activeTour, isTourRunning } = useTour();
  const navigate = useNavigate();

  /*=========================
    GOOGLE MAPS 
    APPLICANT ADDRESS LINK
  ===========================*/
  const [applicantMapLink, setApplicantMapLink] = useState<string>("");

  useEffect(() => {
    // Construct the entire address of the applicant
    // to be used as a query parameter for Google Maps
    const address: string = TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.address || "";
    const addressDetails: string = handleApplicantCityAndState(
      TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.city,
      TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.state,
      TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.zip_code,
    );

    const addressFinalString: string = `${address ? `${address} ` : ""}${addressDetails}`;
    setApplicantMapLink(addressFinalString);
  }, [TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA]);

  // Prevent users from accessing this page if the tour is not currently running
  useEffect(() => {
    if (!activeTour || activeTour.tourId !== "applicants-ai-summary") navigate("/applications/");
  }, [isTourRunning]);

  return (
    <div className="container py--25 applications-details">
      <div className="row mb--20 mb--md--10">
        <div className="col-12 col-lg-8 mb--20">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start">
            <span className="d-flex align-items-center fw--medium txt--lg txt--link mb--md--15">
              <BackIcon className="mr--5" />
              <span>Go Back</span>
            </span>

            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center w--sm-100">
              {user.role === UserRoleNames.SUPER_ADMIN ||
              user.role === UserRoleNames.ACCOUNT_MANAGER ? (
                <Button modifierClass="btn--sm btn--danger mr--10 mb--md--15" isDisabled={true}>
                  Hide Application
                </Button>
              ) : null}

              <PermissionCheckComponentWrapper permissions={["buckets_view"]}>
                <ApplicationBucketMovement
                  key={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.bucket[0]}
                  title={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.bucket[0]}
                  preselectedBucket={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.bucket[0]}
                  id={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.id}
                  handleApplicationsResetSelection={() => undefined}
                  name={handleFullnameCombination(TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant)}
                  isDisabled={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.is_hidden}
                />
              </PermissionCheckComponentWrapper>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-4 d-flex justify-content-end pr--30">
          <div className="applications-details__header__icons">
            <Tooltip text="Email Application" size="md">
              <EmailIcon
                className="applications-details__header__icon"
                data-testid="button:share-email"
              />
            </Tooltip>
            <Tooltip text="Print Application" size="md" wrapperModifierClass="ml--10">
              <Link to="/applications/" target="_blank" data-testid="button:print">
                <PrintIcon className="applications-details__header__icon" />
              </Link>
            </Tooltip>

            <Tooltip text="Refresh Page" size="md" wrapperModifierClass="ml--10">
              <RefreshIcon
                className="applications-details__header__icon"
                data-testid="button:refresh"
              />
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-lg-8">
          {/* APPLICANT DETAILS */}
          <Card
            modifierClass={`card--rounded ${
              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.is_hidden ? "card--hidden" : ""
            }`}
          >
            <div className="row">
              <div className="col-12 col-md-3">
                <div className="d-flex flex-column justify-content-center align-items-center mb--md--15">
                  <ApplicationRating
                    comments_avg_rating={
                      TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.comments_avg_rating ?? null
                    }
                  />
                  <ApplicationPhoto
                    name={handleFullnameCombination(TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant)}
                    photo={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant?.photo ?? ""}
                    sbca={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.sbca?.score ?? null}
                  />

                  <ApplicationDateSection
                    active_bucket={null}
                    applicant_hired_date={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.hired_date}
                    applicant_name={`${TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.first_name} ${TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.last_name}`}
                    application_id={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.id}
                    registration_date={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.registration_date}
                    applicant_bucket={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.bucket[0]}
                  />
                </div>
              </div>

              <div className="col-12 col-md-9">
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center align-items-sm-start mb--20">
                  <div className="applications__details">
                    <h4
                      className="applications__details__name break-word"
                      title={handleFullnameCombination(
                        TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant,
                      )}
                    >
                      {handleFullnameCombination(TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant)}
                    </h4>

                    <p
                      className="applications__details__position break-word"
                      title={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.position}
                    >
                      {TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.position || "N/A"}
                    </p>
                  </div>

                  <PermissionCheckComponentWrapper permissions={["buckets_view"]}>
                    <div className="d-flex align-items-center">
                      <span className="mr--15">
                        <ApplicationArchive
                          id={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.id}
                          currentBuckets={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.bucket}
                          name={handleFullnameCombination(
                            TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant,
                          )}
                        />
                      </span>

                      <ApplicationFavorite
                        id={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.id}
                        currentBuckets={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.bucket}
                        name={handleFullnameCombination(
                          TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant,
                        )}
                      />
                    </div>
                  </PermissionCheckComponentWrapper>
                </div>

                <div className="row no-gutters mb--35 mb--sm--15">
                  <div className="col-12 col-sm-6 mb--sm--15">
                    <div className="applications__contact__location">
                      <LocationIcon className="mr--5" />

                      {applicantMapLink ? (
                        <a
                          href={`https://maps.google.com/maps?q=${applicantMapLink}`}
                          target="_blank"
                          rel="noopener"
                          className="pr--30"
                          title={applicantMapLink}
                        >
                          <span className="d-block mb--10">
                            {TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.address || "N/A"}
                          </span>
                          <span className="d-block m--0">
                            {handleApplicantCityAndState(
                              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.city,
                              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.state,
                              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.zip_code,
                            )}
                          </span>
                        </a>
                      ) : (
                        <p className="mb--0 pr--30">
                          <span className="d-block mb--10">
                            {TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.address || "N/A"}
                          </span>
                          <span className="d-block m--0">
                            {handleApplicantCityAndState(
                              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.city,
                              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.state,
                              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.zip_code,
                            ) || "N/A"}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 mb--sm--15">
                    <div className="d-flex flex-column">
                      <div
                        className="applications__contact__phone"
                        title={
                          handlePhoneStringSanitization(
                            TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.phone,
                          ) || "N/A"
                        }
                      >
                        <PhoneIcon className="mr--5" />

                        {TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant?.phone ? (
                          <a
                            href={`tel:${handlePhoneStringSanitization(
                              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.phone,
                            )}`}
                            className="break-word"
                          >
                            {handlePhoneStringSanitization(
                              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.phone,
                            )}
                          </a>
                        ) : (
                          <p className="m--0">N/A</p>
                        )}
                      </div>

                      {TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.email ? (
                        <a
                          href={`mailto: ${TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.email}`}
                          className="break-word"
                        >
                          <ApplicationMailIcon className="applications__contact__email" />{" "}
                          {TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.email}
                        </a>
                      ) : (
                        <p className="m--0">N/A</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* APPLICATION SOURCE */}
                <ApplicationSourceAdmin
                  source={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.source}
                  modifierClass="mb--20"
                />

                <hr className="mb--20" />

                <div className="d-flex align-items-center justify-content-center justify-content-md-start flex-wrap">
                  <ApplicationSendText
                    application_id={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.id}
                    applicant_details={{
                      id: TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.id,
                      text_message_consent:
                        TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.text_message_consent,
                      name: handleFullnameCombination(
                        TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant,
                      ),
                      photo: TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.photo ?? null,
                      phone: TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.phone,
                    }}
                  />

                  <ApplicationResume
                    resume={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.resume}
                    tooltipText={
                      TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.resume
                        ? "Download Resume"
                        : TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.resume_request_date
                        ? `Resume Requested On ${format(
                            new Date(TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.resume_request_date),
                            "MM/dd/yyyy",
                          )}. Click to Request Again`
                        : "Request Resume"
                    }
                    handleResume={() => undefined}
                  ></ApplicationResume>

                  <ApplicationLinkedIn
                    linkedin={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.linkedin ?? ""}
                  />

                  <ApplicationSendEmail
                    details={{
                      applicantName: `${TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.first_name} ${TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.last_name}`,
                      applicantEmail: TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.email,
                      applicationDate: TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.registration_date,
                      applicationPosition: TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.position,
                      applicantLocation: `${TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.city}, ${TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.state}`,
                    }}
                  />

                  <ApplicationScheduleMeeting
                    meeting_type="video"
                    tooltipText={
                      TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.schedule_meeting
                        ? "Start Video Interview"
                        : "Video Interview"
                    }
                    handleScheduleMeeting={() => undefined}
                  />
                </div>
              </div>
            </div>
          </Card>

          <div id="applicant-details-wrapper">
            {/* APPLICANT SUMMARY */}
            <ApplicantSummary
              application={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA}
              isOpenByDefault={false}
            />

            {/* SBCA RESULTS DETAILS */}
            <ApplicationSBCADetails
              application={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA}
              isOpenByDefault={false}
            />

            {/* INTERVIEW QUESTIONS */}
            <ApplicationGeneratedInterviewQuestions
              ai_sbca_summary={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.ai_sbca_summary}
              ai_resume_summary={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.ai_resume_summary}
            />

            {/* EMPLOYMENT HISTORY */}
            <ApplicationExtraInformation
              form_name={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.form_name}
              extra_info={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.applicant.custom_values}
              is_hidden={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.is_hidden}
              isOpenByDefault={false}
            />
          </div>
        </div>

        <div className="col-12 col-lg-4">
          {/* ADMIN ACTIONS */}
          {user.role === UserRoleNames.SUPER_ADMIN ? (
            <ApplicationsDetailsAdminActions application={TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA} />
          ) : null}

          {/* SCHEDULE APPOINTMENTS */}
          <PermissionCheckComponentWrapper permissions={["applicant_appointments_create"]}>
            <Card
              modifierClass={`card--rounded ${
                TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.is_hidden ? "card--hidden" : ""
              }`}
            >
              <h2 className="fw--semibold mb--20">Schedule Interviews</h2>
            </Card>
          </PermissionCheckComponentWrapper>

          {/* APPOINTMENTS */}
          <PermissionCheckComponentWrapper permissions={["applicant_appointments_view"]}>
            <Card
              modifierClass={`card--rounded ${
                TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.is_hidden ? "card--hidden" : ""
              }`}
            >
              <h2 className="d-flex align-items-center fw--semibold mb--20">Interviews</h2>

              {TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA &&
              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.appointments.length > 0 ? (
                TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.appointments.map(
                  (appointment: AppointmentResponseFields) => (
                    <AppointmentsListItem
                      key={appointment.id}
                      details={{ ...appointment, applicationID }}
                    />
                  ),
                )
              ) : (
                <h6>No scheduled interviews.</h6>
              )}
            </Card>
          </PermissionCheckComponentWrapper>

          {/* COMMENTS */}
          <Card
            modifierClass={`card--rounded ${
              TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.is_hidden ? "card--hidden" : ""
            }`}
          >
            <h2 className="d-flex align-items-center fw--semibold mb--20">Comments</h2>

            {TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA &&
            TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.comments.length > 0 ? (
              <AnimatePresence initial={false}>
                {TOUR_APPLICANTS_AI_SUMMARY_FAKE_DATA.comments.map(
                  (comment: ApplicationCommentsFields, index: number) => (
                    <ApplicationComment
                      key={`applications-details-comment-${index}`}
                      index={index}
                      applicationID={applicationID}
                      comment={comment}
                      handleCommentEdit={() => undefined}
                    />
                  ),
                )}
              </AnimatePresence>
            ) : (
              <h6 className="mb--10">No comments available.</h6>
            )}

            <div className="d-flex justify-content-end mt--20">
              <Button modifierClass="btn--fluid btn--pill btn--transparent btn--border btn--border--primary mb--20">
                Add Comment
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* TOUR_FLAG[applicants-ai-summary] */}
      <ProductTourWrapper tourToShow={"applicants-ai-summary"} isLoading={false} />
    </div>
  );
};

export default TourAiSummary;
